import { useState, useEffect, useCallback } from 'react'
import { useAppStore } from '../../store/appStore'
import { startPairProgramming, stopPairProgramming, isPairActive, onCodeChange, type PairSuggestion } from '../../services/pairProgramming'

const typeIcons: Record<string, string> = {
  bug: '🐛',
  refactor: '♻️',
  completion: '💡',
  tip: '💡',
  warning: '⚠️',
}

const typeColors: Record<string, string> = {
  bug: 'var(--red)',
  refactor: 'var(--accent)',
  completion: 'var(--accent-2)',
  tip: 'var(--accent-3)',
  warning: 'var(--accent-3)',
}

export function PairProgrammingPanel() {
  const [suggestions, setSuggestions] = useState<PairSuggestion[]>([])
  const [active, setActive] = useState(isPairActive())
  const activeTab = useAppStore(s => s.openTabs.find(t => t.id === s.activeTabId))

  const handleSuggestion = useCallback((s: PairSuggestion) => {
    setSuggestions(prev => [s, ...prev].slice(0, 5)) // Keep last 5
  }, [])

  const toggle = () => {
    if (active) {
      stopPairProgramming()
      setActive(false)
      setSuggestions([])
    } else {
      startPairProgramming(handleSuggestion)
      setActive(true)
    }
  }

  // Watch for code changes in active tab
  useEffect(() => {
    if (!active || !activeTab) return
    onCodeChange(activeTab.content, activeTab.language, activeTab.path)
  }, [active, activeTab?.content, activeTab?.language, activeTab?.path])

  const dismiss = (id: string) => {
    setSuggestions(prev => prev.filter(s => s.id !== id))
  }

  return (
    <div style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-1)', flexShrink: 0 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px', borderBottom: suggestions.length ? '1px solid var(--border)' : 'none' }}>
        <span style={{ fontSize: 12 }}>🤖</span>
        <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--text-1)' }}>AI Pair</span>
        <div style={{ flex: 1 }} />
        <button onClick={toggle} style={{
          padding: '2px 8px', borderRadius: 10, fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 700, cursor: 'pointer',
          border: `1px solid ${active ? 'var(--accent-2)' : 'var(--border-2)'}`,
          background: active ? 'rgba(95,212,164,0.1)' : 'none',
          color: active ? 'var(--accent-2)' : 'var(--text-2)',
        }}>
          {active ? '● ON' : '○ OFF'}
        </button>
      </div>

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div style={{ maxHeight: 120, overflowY: 'auto' }}>
          {suggestions.map(s => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 12px', borderBottom: '1px solid var(--border)', fontSize: 11, fontFamily: 'var(--font-mono)' }}>
              <span style={{ fontSize: 12, flexShrink: 0 }}>{typeIcons[s.type] ?? '💡'}</span>
              <div style={{ flex: 1 }}>
                <span style={{ color: typeColors[s.type] ?? 'var(--text-1)' }}>{s.message}</span>
                {s.line && <span style={{ color: 'var(--text-2)', marginLeft: 6, fontSize: 9 }}>L{s.line}</span>}
              </div>
              <button onClick={() => dismiss(s.id)} style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 9, padding: 2 }}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
